
/**
 * Module dependencies.
 */

import { ComponentType, ForwardedRef, MouseEvent, ReactNode, forwardRef } from 'react';
import { Icon } from 'react-components/media';
import { Loading } from '../loading';
import { RouterLink } from '../links/router-link';
import { buttonFontSizes, colorThemes } from './styles';
import { get } from 'lodash';
import { ifProp, switchProp, theme } from 'styled-tools';
import { typography } from '../typography/typography-styles';
import styled, { css } from 'styled-components';

/**
 * `AlignIcon` type.
 */

type AlignIcon = 'left' | 'right';

/**
 * `Size` type.
 */

type Size = 'large' | 'medium' | 'small';

/**
 * Export `ButtonProps` type.
 */

export type ButtonProps = {
  alignIcon?: 'left' | 'right';
  as?: string | ComponentType<any>;
  children: ReactNode;
  colorTheme?: keyof typeof colorThemes;
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  icon?: string;
  iconColor?: string;
  iconSize?: Size | string;
  isLoading?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  rel?: string;
  role?: string;
  size?: Size;
  spinner?: {
    size?: number;
    stroke?: number;
  };
  target?: string;
  to?: string | {
    pathname: string;
    state: any;
  };
  type?: 'button' | 'submit';
};

/**
 * Button themes.
 */

const buttonThemes = Object.entries(colorThemes).reduce((result, [themeName, themeSchema]) => ({
  ...result,
  [themeName]: css`
    background-color: ${get(themeSchema, 'backgroundColor')};
    color: ${get(themeSchema, 'color')};

    ${ifProp('disabled', css`
      background-color: ${get(themeSchema, 'disabled.backgroundColor')};
      color: ${get(themeSchema, 'disabled.color')};
    `)}

    &:focus,
    &:hover {
      background-color: ${get(themeSchema, 'interaction.backgroundColor')};
    }
  `
}), {});

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.button.attrs<Pick<ButtonProps, 'as' | 'href' | 'to' | 'type'>>(({ as, href, to, type }) => {
  const element = as || to && RouterLink || href && 'a' || 'button';

  return {
    as: element,
    type: type || (element === 'button' ? 'button' : undefined)
  };
})`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  border: none;
  border-radius: ${theme('dimensions.borderRadius')}px;
  cursor: pointer;
  display: flex;
  font-family: ${typography.fontFamilies.saira};
  justify-content: center;
  outline: none;
  position: relative;
  text-decoration: none;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color;
  white-space: nowrap;

  ${switchProp('colorTheme', buttonThemes)}

  ${switchProp('size', {
    large: css`
      ${buttonFontSizes.large}

      padding: 15px 16px;
    `,
    medium: css`
      ${buttonFontSizes.medium}

      padding: 9px 12px;
    `,
    small: css`
      ${buttonFontSizes.small}

      padding: 7px 12px;
    `
  })};

  ${ifProp('fullWidth', css`
    width: 100%;
  `)}

  ${ifProp('disabled', css`
    cursor: default;
    pointer-events: none;
  `)}

  &:focus,
  &:hover {
    text-decoration: none;
  }
`;

/**
 * `Content` styled component.
 */

const Content = styled.div<{ alignIcon?: AlignIcon; isLoading: boolean }>`
  align-items: center;
  column-gap: 8px;
  display: flex;
  justify-content: center;
  transition: opacity ${theme('animations.defaultTransition')};

  ${switchProp('alignIcon', {
    right: css`
      flex-direction: row-reverse;
    `
  })}

  ${ifProp('isLoading', css`
    opacity: 0;
  `)}
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)`
  transition: color ${theme('animations.defaultTransition')};
`;

/**
 * Get icon size.
 */

function getIconSize(size: Size | string): string {
  switch (size) {
    case 'large':
      return '24px';

    case 'medium':
    case 'small':
      return '16px';

    default:
      return size;
  }
}

/**
 * `Button` component.
 */

function Button(props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>): JSX.Element {
  const {
    alignIcon,
    children,
    colorTheme = 'primary',
    icon,
    iconColor,
    iconSize,
    isLoading = false,
    size = 'small',
    spinner,
    ...rest
  } = props;

  return (
    <Wrapper
      {...rest}
      colorTheme={colorTheme}
      ref={ref}
      size={size}
    >
      <Content
        alignIcon={alignIcon}
        isLoading={isLoading}
      >
        {!!icon && !!iconSize && (
          <StyledIcon
            aria-hidden
            color={iconColor ?? 'inherit'}
            icon={icon}
            size={getIconSize(iconSize)}
          />
        )}

        {children}
      </Content>

      <Loading
        {...spinner}
        active={isLoading}
      />
    </Wrapper>
  );
}

/**
 * Forwarded `Button`.
 */

const ForwardedButton = forwardRef<HTMLButtonElement, ButtonProps>(Button);

/**
 * Export `Button` component.
 */

export { ForwardedButton as Button };
